import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaFacebookF, FaInstagram, FaWhatsapp, FaMapMarkerAlt, FaEnvelope } from 'react-icons/fa';

const FooterSection = styled.footer`
  background-color: var(--color-secondary);
  padding-top: 4rem;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  font-family: var(--font-primary);
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-accent);
  margin-bottom: 1.5rem;
  
  span {
    color: var(--color-text);
  }
`;

const FooterText = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const FooterTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.8rem;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 40px;
    height: 2px;
    background-color: var(--color-accent);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.8rem;
`;

const StyledLink = styled.a.attrs({
  as: Link
})`
  color: var(--color-text-secondary);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: var(--color-accent);
    padding-left: 5px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ContactIcon = styled.div`
  color: var(--color-accent);
  margin-right: 1rem;
  font-size: 1rem;
  margin-top: 0.2rem;
`;

const ContactText = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--color-accent);
    color: var(--color-primary);
    transform: translateY(-3px);
  }
`;

const BottomBar = styled.div`
  padding: 1.5rem 0;
  text-align: center;
`;

const Copyright = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  
  a {
    color: var(--color-accent);
    transition: all 0.3s ease;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ScrollToTop = styled.button.attrs({
  as: motion.button
})`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 45px;
  height: 45px;
  background-color: var(--color-accent);
  color: var(--color-primary);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 99;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '↑';
    font-size: 1.2rem;
    font-weight: bold;
  }
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <FooterSection>
      <FooterContainer>
        <FooterContent>
          <FooterColumn>
            <FooterLogo>
              My <span>Dream</span> Suits
            </FooterLogo>
            <FooterText>
              Especialistas em ternos e smokings de alta qualidade para noivos, padrinhos, formaturas e eventos especiais. Elegância e sofisticação para seus momentos mais importantes.
            </FooterText>
            <SocialLinks>
              <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <FaFacebookF />
              </SocialLink>
              <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <FaInstagram />
              </SocialLink>
              <SocialLink href="https://wa.me/5527988340277" target="_blank" rel="noopener noreferrer">
                <FaWhatsapp />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Links Rápidos</FooterTitle>
            <FooterLinks>
              <FooterLink>
                <StyledLink to="hero" smooth={true} duration={500} spy={true} offset={-70}>Início</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="about" smooth={true} duration={500} spy={true} offset={-70}>Sobre</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="services" smooth={true} duration={500} spy={true} offset={-70}>Serviços</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="products" smooth={true} duration={500} spy={true} offset={-70}>Produtos</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="gallery" smooth={true} duration={500} spy={true} offset={-70}>Galeria</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="contact" smooth={true} duration={500} spy={true} offset={-70}>Contato</StyledLink>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Produtos</FooterTitle>
            <FooterLinks>
              <FooterLink>Ternos Slim</FooterLink>
              <FooterLink>Smokings</FooterLink>
              <FooterLink>Ternos para Noivos</FooterLink>
              <FooterLink>Ternos para Padrinhos</FooterLink>
              <FooterLink>Ternos para Formaturas</FooterLink>
              <FooterLink>Acessórios</FooterLink>
            </FooterLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Contato</FooterTitle>
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>
                R. Getúlio Vargas, 217 - loja 02 - Glória, Vila Velha - ES, 29122-030
              </ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FaWhatsapp />
              </ContactIcon>
              <ContactText>(27) 98834-0277</ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactText>contato@mydreammodafesta.com.br</ContactText>
            </ContactItem>
          </FooterColumn>
        </FooterContent>
        
        <BottomBar>
          <Copyright>
            &copy; {new Date().getFullYear()} My Dream Suits. Todos os direitos reservados. 
            Desenvolvido por <a href="#" target="_blank" rel="noopener noreferrer">Sua Empresa</a>
          </Copyright>
        </BottomBar>
      </FooterContainer>
      
      <ScrollToTop
        onClick={scrollToTop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.1 }}
      />
    </FooterSection>
  );
};

export default Footer; 